import ImprimirButton from '@/global/components/DataGrid/components/CustomGridToolBar/ImprimirButton'
import { GridButton } from '@/global/components/DataGrid/components/CustomGridToolBar/MenuComponents'
import { FooterAviso } from '@/global/components/DataGrid/components/DataTable/Footer'
import useDefaultFilterModel from '@/global/components/DataGrid/hooks/useDefaultFilterModel'
import useDefaultSortModel from '@/global/components/DataGrid/hooks/useDefaultSortModel'
import FilterModelPro from '@/global/components/DataGrid/utils/filterModelPro'
import {
    EscopoFuncionario,
    useEscopoFuncionario,
} from '@/global/components/EscopoFuncionario'
import { Link } from '@/global/components/Link'
import { useFilterLocaisCentroStore } from '@/global/components/Selects/filter-select'
import { hasPermission } from '@/hooks/useBackendMenu'
import SelectCompetencia, {
    useCompetencia,
} from 'global/components/Selects/SelectCompetencia'
import ContentGuide from 'global/guides/ContentGuide'
import { HistoryIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Ajustes, ajustes } from 'service/api'
import { useStore } from 'store'
import { adaptadorAjustes } from 'templates/Relatorios/Ajustes/adaptador-ajustes'
import { colunasParaNiveis } from 'templates/Relatorios/Ajustes/colunas-dinamicas'
import { explodirLinhas } from 'templates/Relatorios/Ajustes/explodir-linhas'
import {
    LABEL_NIVEL,
    NivelAjuste,
    ORDEM_NIVEIS,
} from 'templates/Relatorios/Ajustes/niveis-agrupamento'

export default function RelatorioAjustes() {
    const [isImprimindo, setIsImprimindo] = useState(false)
    const [competencia, setCompetencia] = useCompetencia()
    const empresaId = useStore(state => state.companyId)
    const [escopo, setEscopo] = useEscopoFuncionario()

    const [niveis, setNiveis] = useState<Set<NivelAjuste>>(new Set())
    const currentColumns = useMemo(() => colunasParaNiveis(niveis), [niveis])
    const [filterModel, setFilterModel] = useDefaultFilterModel()
    const [sortModel, setSortModel, sort] = useDefaultSortModel(currentColumns)

    const { data, isLoading } = ajustes.relatorio.useFetch(false, {
        queryParams: {
            empresaId,
            competencia,
        },
        enabled: empresaId > 0,
    })

    const useFilter = useFilterLocaisCentroStore()
    const linhasNoEscopo = useMemo(() => {
        return new Ajustes(data).ajustes.filter(useFilter).sort((a, b) => {
            const nomeA = a.Funcionario?.nome ?? ''
            const nomeB = b.Funcionario?.nome ?? ''
            return nomeA.localeCompare(nomeB)
        })
    }, [data, useFilter])

    const rows = useMemo(
        () => explodirLinhas(linhasNoEscopo, niveis),
        [linhasNoEscopo, niveis],
    )

    const groupingModel = useMemo(
        () => adaptadorAjustes.obterGroupingModel(niveis),
        [niveis],
    )

    const handleImprimir = async () => {
        setIsImprimindo(true)
        try {
            const filtrarLinha = new FilterModelPro(filterModel, currentColumns)
                .filter
            const filtradas = sort(rows.filter(filtrarLinha))

            await ajustes.pdf({
                data: filtradas.map(ajuste => ajuste.id),
                queryParam: {
                    empresaId,
                    competencia,
                    niveis: Array.from(niveis).join(','),
                },
            })
        } finally {
            setIsImprimindo(false)
        }
    }

    return (
        <ContentGuide>
            <ContentGuide.Card>
                <ContentGuide.Header label={['Relatórios', 'Ajustes']}>
                    <EscopoFuncionario.Root value={escopo} onChange={setEscopo}>
                        <EscopoFuncionario.Cascata>
                            <EscopoFuncionario.Trigger />
                            <EscopoFuncionario.Empresa />
                            <EscopoFuncionario.CentrosCusto multiple />
                            <EscopoFuncionario.Locais multiple />
                            <EscopoFuncionario.Funcionarios multiple />
                        </EscopoFuncionario.Cascata>
                    </EscopoFuncionario.Root>
                    <ContentGuide.Filtros>
                        <SelectCompetencia
                            onChange={setCompetencia}
                            value={competencia}
                        />
                    </ContentGuide.Filtros>
                </ContentGuide.Header>
                <ContentGuide.DataGrid
                    rows={rows}
                    columns={currentColumns}
                    filterModel={filterModel}
                    sortModel={sortModel}
                    enableRowGrouping
                    groupingModel={groupingModel}
                    getRowCanExpand={row =>
                        adaptadorAjustes.podeExpandirLinha(
                            row,
                            groupingModel.length,
                        )
                    }
                    loading={isLoading}
                    onFilterModelChange={setFilterModel}
                    onSortModelChange={setSortModel}
                    components={{
                        Toolbar: () => [
                            hasPermission(
                                ['read', 'AssinaturaEletrônicaDeAjustes'],
                                () => (
                                    <GridButton
                                        key="historico"
                                        href="impressos"
                                        startIcon={<HistoryIcon />}
                                        label="Histórico"
                                    />
                                ),
                            ),
                            <div key="flex" style={{ flex: 1 }} />,
                            <ImprimirButton
                                key="imprimir"
                                handleClick={handleImprimir}
                                isImprimindo={isImprimindo}
                            />,
                        ],
                        Footer: () => (
                            <FooterAviso>
                                Os relatórios gerados nessa tela não possuem
                                assinatura eletrônica ou validade jurídica.
                                {hasPermission([
                                    'create',
                                    'AssinaturaEletrônicaDeAjustes',
                                ]) && (
                                    <span>
                                        {' '}
                                        Para baixar os relatórios assinados,
                                        acesse{' '}
                                        <Link href="impressos">Impressos</Link>.
                                    </span>
                                )}
                            </FooterAviso>
                        ),
                    }}
                    tableId="relatorio-ajustes"
                    agrupamento={{
                        options: ORDEM_NIVEIS.map(n => ({
                            value: n,
                            label: LABEL_NIVEL[n],
                        })),
                        selected: niveis,
                        onChange: selected =>
                            setNiveis(selected as Set<NivelAjuste>),
                    }}
                    ToolbarProps={{
                        exportButtonProps: {
                            fileName: 'exportacao-ajustes-realizados',
                        },
                        showVisibilityButton: true,
                    }}
                />
            </ContentGuide.Card>
        </ContentGuide>
    )
}
